// /help: public (outside signers have no account), so it never touches Cognito.
// Keep the numbers here in step with services/api/src/lib/limits.ts and tenant.ts (plans).
import { useEffect, type ReactNode } from "react";
import { ArrowRight, BookOpen, Building2, CircleHelp, FileSignature, KeyRound, LifeBuoy, PenLine, Plug, ShieldCheck } from "lucide-react";
import { Lockup, Logo } from "../components/icons";

const SECTIONS = [
  { id: "signing", label: "Signing a document", icon: PenLine },
  { id: "sending", label: "Sending for signature", icon: FileSignature },
  { id: "tracking", label: "Tracking and managing", icon: BookOpen },
  { id: "company", label: "Your company account", icon: Building2 },
  { id: "sso", label: "Single sign-on (Entra ID)", icon: KeyRound },
  { id: "connectors", label: "Connectors (CRM, storage)", icon: Plug },
  { id: "security", label: "Security and the signed PDF", icon: ShieldCheck },
  { id: "troubleshooting", label: "Troubleshooting", icon: LifeBuoy },
  { id: "limits", label: "Limits at a glance", icon: CircleHelp },
] as const;

export default function Help() {
  useEffect(() => {
    document.title = "Help · eSignIt";
    // Deep links (/help#signing) land on the right section once rendered.
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, []);

  return (
    <div className="min-h-full flex flex-col bg-bg">
      <div className="brand-bar h-1 shrink-0" aria-hidden />
      <header className="bg-surface border-b border-line px-4 h-14 flex items-center gap-3 shrink-0 sticky top-0 z-10">
        <a href="/" className="flex items-center gap-3" aria-label="eSignIt home">
          <Logo className="size-7 sm:hidden" />
          <span className="max-sm:hidden">
            <Lockup height={17} />
          </span>
        </a>
        <span className="text-sm text-muted">Help</span>
        <a href="/" className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline underline-offset-2">
          Open eSignIt <ArrowRight className="size-4" />
        </a>
      </header>

      <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-10">
        <nav aria-label="Help sections" className="max-lg:mb-8">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">On this page</div>
            <ul className="flex lg:flex-col flex-wrap gap-1">
              {SECTIONS.map(({ id, label, icon: Icon }) => (
                <li key={id}>
                  <a href={`#${id}`} className="flex items-center gap-2 rounded-lg px-2.5 h-9 text-sm text-ink-2 hover:bg-surface-2 max-lg:border max-lg:border-line max-lg:bg-surface">
                    <Icon className="size-4 text-accent-text shrink-0" /> {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="min-w-0 max-w-3xl">
          <h1 className="text-3xl font-bold">eSignIt help</h1>
          <p className="text-ink-2 mt-3 leading-relaxed">
            eSignIt lets companies send PDFs for electronic signature. The people signing don't need an account: they get an email link, confirm a code, and sign in their browser. When everyone has signed, every party receives the
            completed, sealed PDF by email, and the company's connected systems (Salesforce, HubSpot, Dynamics 365, SharePoint, S3, Infor or a webhook) get it too.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            <Shortcut href="#signing" title="I was sent something to sign" text="Open the link in your email; no account needed." />
            <Shortcut href="#company" title="I want to use eSignIt at my company" text="Register your company, invite colleagues, connect your systems." />
          </div>

          <Section id="signing" title="Signing a document">
            <p>You don't need an account or any software, only the email the document was sent to and a web browser on a computer, tablet or phone.</p>
            <Steps
              items={[
                <>
                  Open the email (it says <i>Sent by …company… via eSignIt</i> and comes from sign@esignit.app) and select <b>Review and sign</b>.
                </>,
                <>
                  Select <b>Email me a code</b>. A 6-digit code arrives at the same address within a minute or so. Enter it to continue. This confirms that the person signing is the person the document was sent to. If you work at the
                  company that sent it and have an eSignIt account, you can select <b>Sign in to eSignIt</b> instead.
                </>,
                <>
                  Read the <b>electronic record and signature disclosure</b> and tick <i>I agree to use electronic records and signatures</i>. If you'd rather not sign electronically, choose <b>Decline to sign</b> and say why.
                </>,
                <>
                  Fill in each highlighted field. Select <b>Next</b> to jump to the next one. For a signature or initials field, <b>adopt</b> your signature: type your name, draw it, or upload a photo or scan (PNG or JPG; the paper
                  background is removed). Your adopted signature is reused for every signature field.
                </>,
                <>
                  When the counter says all required fields are done, select <b>Finish</b>. You'll get the completed PDF by email once everyone has signed.
                </>,
              ]}
            />
            <Faq q="Some fields are already filled in. Can I change them?">If others signed before you, their signatures and entries show read-only so you can see what you're countersigning. Only the fields highlighted in colour are yours.</Faq>
            <Faq q="Can I forward the link to someone else?">Please don't: the link is personal, and the code is always emailed to the original address, so the link alone can't open the document. Ask the sender to correct the recipient instead.</Faq>
            <Faq q="The page says it's not my turn yet.">The sender chose a signing order and someone before you hasn't signed yet. You'll get another email when it's your turn.</Faq>
            <Faq q="I don't agree with the document.">Reply to the email to reach the sender (replies go straight to them), or choose <b>Decline to sign</b> with a reason. Declining stops the whole envelope.</Faq>
            <Faq q="Where's my copy?">When the last person signs, you're emailed the completed PDF (or a download link for files over 10 MB). For 30 days you can also download it again from your original link, after a new code.</Faq>
          </Section>

          <Section id="sending" title="Sending for signature">
            <p>
              Sign in at this site with your work email. If your company uses single sign-on you'll go straight to it; otherwise use your eSignIt password. Not set up yet? <a href="/signup">Register your company</a>, or ask your company admin to invite you.
            </p>
            <Steps
              items={[
                <>
                  Select <b>New envelope</b> and upload a <b>PDF</b>. Word documents need saving as PDF first. Password-protected PDFs aren't accepted. Fillable form fields are flattened so nobody can change the underlying text.
                </>,
                <>
                  Add <b>recipients</b>: name and email for each. Choose <b>Signs</b> or <b>Gets a copy</b>. Select <b>Add me</b> if you also need to sign; you'll sign in the app. Choose <b>in order</b> or <b>all at once</b>.
                </>,
                <>
                  Pick a recipient, then a field type (signature, initials, date signed, name, text, checkbox), and click on the page to place it. Drag to move, use the handle to resize, or copy a field to every page.
                </>,
                <>
                  Optionally <b>link a CRM record</b> (Salesforce, HubSpot, Dynamics 365 or Infor, if your company connected one) so the signed PDF is filed there.
                </>,
                <>
                  Select <b>Send</b>, check the subject, message, expiry and reminders, confirm the document is suitable for e-signature, and send.
                </>,
              ]}
            />
            <Callout title="Not every document can be signed electronically">
              Don't use e-signatures for wills, codicils or testamentary trusts; court orders or filings; notarised documents; adoption, divorce or other family-law documents; real-estate documents that must be recorded on paper; or
              notices of utility shut-off, foreclosure, eviction, insurance cancellation or product recalls. In the EU this is a simple electronic signature under eIDAS. This isn't legal advice.
            </Callout>
          </Section>

          <Section id="tracking" title="Tracking and managing">
            <p>Your dashboard shows every envelope you sent and where it is. Open one to see each recipient's progress and the full activity history.</p>
            <dl className="mt-3 space-y-2">
              <Def term="Remind">Emails everyone whose turn it is (at most once an hour). Automatic reminders go out on the schedule you chose.</Def>
              <Def term="Correct a recipient">Fix a wrong email before they sign; their old link stops working and the new address gets a fresh one.</Def>
              <Def term="Void">Cancels the envelope with a reason the signers see.</Def>
              <Def term="Audit trail">Download every event (sent, opened, code verified, signed…) as CSV.</Def>
              <Def term="Connectors">The envelope page shows what each connected system did with the signed PDF, with Retry if something failed.</Def>
            </dl>
          </Section>

          <Section id="company" title="Your company account">
            <Steps
              items={[
                <>
                  <a href="/signup">Register your company</a> with your work email. You become its <b>owner</b>. Confirm your email with the code we send, then sign in.
                </>,
                <>
                  eSignIt reviews new companies before they can send (usually within one business day). Meanwhile you can invite people, set up single sign-on and connectors, and prepare drafts.
                </>,
                <>
                  Under <b>Company → People</b>, invite colleagues as <b>Senders</b> (send their own envelopes) or <b>Admins</b> (also manage people, sign-on, connectors and settings, and see every envelope's status). Owners can manage
                  other owners.
                </>,
                <>
                  Under <b>Branding & settings</b>, add your logo and colour (signers see them on emails, signing pages and the certificate), default expiry and reminders, how long completed documents stay locked, and whether admins can open
                  everyone's documents.
                </>,
              ]}
            />
            <p className="mt-3">Plans: Free (5 envelopes a month, 1 person, webhooks), Team (100 a month, 10 people, single sign-on, all connectors) and Business (1,000 a month, unlimited people). Contact eSignIt to change plans.</p>
          </Section>

          <Section id="sso" title="Single sign-on (Entra ID)">
            <p>Team and Business companies can sign in with their own Microsoft Entra ID (Azure AD), by OpenID Connect (recommended) or SAML.</p>
            <Steps
              items={[
                <>
                  <b>Verify your domain</b>: add the TXT record shown under Company → Single sign-on to your DNS, then select Check now. A verified domain can't be registered by anyone else.
                </>,
                <>
                  <b>OpenID Connect</b>: create an app registration in Entra with the redirect URI shown, add the <i>email</i> optional claim, create a client secret, and paste the tenant ID, client ID and secret. <b>SAML</b>: create a
                  non-gallery enterprise app with the Entity ID and Reply URL shown, and paste its App Federation Metadata Url.
                </>,
                <>
                  Select <b>Test sign-in</b>. A pop-up signs you in through Entra and reports back.
                </>,
                <>
                  Optionally <b>require single sign-on</b> (password sign-in is refused for your domains) and <b>let anyone from Entra in</b> as a Sender on first sign-in.
                </>,
              ]}
            />
          </Section>

          <Section id="connectors" title="Connectors (CRM, storage)">
            <p>Connectors send completed documents to your systems. Admins add them under Company → Connectors.</p>
            <dl className="mt-3 space-y-2">
              <Def term="Salesforce">Sign in to your org. On completion: signed PDF in the linked record's Files, an optional field set (e.g. Signature_Completed__c = Yes), and an optional Chatter post. Add a "Send with eSignIt" button using the URL shown.</Def>
              <Def term="HubSpot">Install eSignIt's app. On completion: the PDF is attached to the linked deal through a note, plus an optional property update.</Def>
              <Def term="Dynamics 365">A Microsoft admin grants consent; add eSignIt as an application user in your environment. On completion: a note with the PDF on the linked record, plus an optional column update.</Def>
              <Def term="SharePoint">A Microsoft admin grants consent and gives eSignIt access to one site. Every signed PDF is saved to the library folder you choose.</Def>
              <Def term="Amazon S3">Create a role in your account that trusts eSignIt's role with your External ID. Every signed PDF and its audit JSON go to your bucket.</Def>
              <Def term="Infor CloudSuite">Upload a service account's .ionapi file. On completion: the PDF is stored in IDM, then an optional Landmark update runs. Senders type the record reference.</Def>
              <Def term="Webhook">An HTTPS POST of the event (completed, declined, voided, expired), signed with HMAC-SHA256, with a 15-minute download link for the PDF.</Def>
            </dl>
            <p className="mt-3">Each delivery is retried for about an hour; if it still fails the sender is emailed and the envelope page offers Retry.</p>
          </Section>

          <Section id="security" title="Security and the signed PDF">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Each company's documents are stored separately and encrypted with that company's own key.</li>
              <li>Signing links are personal and verified with a code sent to the same address. Every step is recorded with time and IP address.</li>
              <li>
                The completed PDF has a <b>Certificate of Completion</b> page and is digitally sealed with a timestamp. Open it in Adobe Reader and check the signature panel: any change after signing shows there. (The seal's certificate is
                eSignIt's own, so Adobe shows its identity as "unknown"; the document-integrity check still works.)
              </li>
              <li>Completed documents are locked against deletion or change for the company's retention period (7 years by default).</li>
              <li>eSignIt staff can see companies and usage, never document contents.</li>
            </ul>
          </Section>

          <Section id="troubleshooting" title="Troubleshooting">
            <Faq q="The code email didn't arrive.">Wait a minute and check spam or quarantine; it comes from sign@esignit.app. You can ask for another code (up to 5 an hour).</Faq>
            <Faq q="I'm told to use single sign-on.">Your company requires it. Go back to sign-in, enter your work email and select Continue: you'll be sent to your company's sign-in.</Faq>
            <Faq q="I can't send: waiting for approval.">New companies are reviewed before they can send. You'll get an email when you're approved.</Faq>
            <Faq q="A connector failed.">Open the envelope to see the error, fix the cause (permissions, a deleted record, an expired connection) and select Retry. Admins can test a connector under Company → Connectors.</Faq>
          </Section>

          <Section id="limits" title="Limits at a glance">
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              <Def term="Documents">PDF, up to 25 MB and 100 pages</Def>
              <Def term="Recipients">Up to 10 per envelope</Def>
              <Def term="Expiry">30 days by default, 1–120</Def>
              <Def term="Reminders">Every 3 days by default</Def>
              <Def term="Code">6 digits, valid 10 minutes, 5 tries</Def>
              <Def term="Signing session">30 minutes of inactivity</Def>
              <Def term="Completed copies">Attached up to 10 MB; downloadable for 30 days</Def>
              <Def term="Ended envelopes">Documents deleted 90 days after void, decline or expiry; audit trail kept</Def>
            </dl>
          </Section>
        </main>
      </div>
      <footer className="border-t border-line bg-surface px-4 py-4 text-xs text-muted text-center">
        © {new Date().getFullYear()} eSignIt · <a href="https://esignit.app/terms">Terms</a> · <a href="https://esignit.app/privacy">Privacy</a>
      </footer>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20 mt-12 [&_a]:text-accent-text [&_a]:underline [&_a]:underline-offset-2">
      <h2 className="display text-xl font-bold text-ink mb-3 pb-2 border-b border-line">{title}</h2>
      <div className="text-ink-2 leading-relaxed">{children}</div>
    </section>
  );
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mt-4 space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 size-6 rounded-full bg-accent text-accent-ink text-xs font-semibold flex items-center justify-center mt-0.5 tabular">{i + 1}</span>
          <div>{it}</div>
        </li>
      ))}
    </ol>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group mt-3 rounded-xl border border-line bg-surface px-4 py-3 [&[open]]:pb-4">
      <summary className="cursor-pointer list-none font-medium text-ink flex items-start gap-2">
        <span className="text-accent-text transition-transform group-open:rotate-90 mt-px" aria-hidden>
          ›
        </span>
        {q}
      </summary>
      <div className="mt-2 pl-4 text-sm">{children}</div>
    </details>
  );
}

function Def({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-ink">{term}</dt>
      <dd className="text-ink-2">{children}</dd>
    </div>
  );
}

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6 rounded-xl bg-warn-soft px-4 py-3 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-1 text-ink-2">{children}</div>
    </div>
  );
}

function Shortcut({ href, title, text }: { href: string; title: string; text: string }) {
  return (
    <a href={href} className="group block rounded-xl border border-line bg-surface p-4 hover:border-accent no-underline">
      <div className="font-semibold text-ink flex items-center gap-1">
        {title} <ArrowRight className="size-4 text-accent-text transition-transform group-hover:translate-x-0.5" />
      </div>
      <div className="text-sm text-muted mt-1">{text}</div>
    </a>
  );
}
